import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Grid,
} from '@mui/material'
import React from 'react'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'

interface TaskDetailsProps {
  handleChange: (
    eventOrValue:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
      | Date
      | null,
    fieldName?: string,
  ) => void
  handleDueDateBlur: () => void
  handleDueDateAccept?: (date: any) => void
  formData: {
    title: string
    details: string
    importance: string
    link: string
    dueDate: Date | null
  }
  error: {
    title: string
    details: string
    importance: string
    link: string
    dueDate: string
  }
}

const TaskDetails: React.FC<TaskDetailsProps> = ({ handleChange, handleDueDateBlur, handleDueDateAccept, formData, error }) => {
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    handleChange(event)
  }

  const handleDateChange = (newValue: any) => {
    handleChange(newValue ? new Date(newValue) : null, 'dueDate')
  }

  const handleLinkBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let value = event.target.value
    
    // Only add https:// when user finishes typing (on blur) and if value doesn't already have protocol
    if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
      value = 'https://' + value
      
      // Create a new event with the modified value
      const modifiedEvent = {
        ...event,
        target: {
          ...event.target,
          value: value,
          name: 'link'
        }
      } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      
      handleChange(modifiedEvent)
    }
  }


  return (
    <Grid container spacing={2} direction='column'>
      <Grid>
        <TextField
          id='title'
          name='title'
          required
          label='Task Title'
          value={formData.title ?? ''}
          onChange={handleChange}
          placeholder='Enter task title'
          helperText={error.title}
          error={!!error.title}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.23)',
            },
          }}
        />
      </Grid>
      <Grid>
        <TextField
          id='details'
          name='details'
          required
          label='Task Description'
          value={formData.details ?? ''}
          onChange={handleChange}
          placeholder='Enter task description'
          multiline
          helperText={error.details}
          error={!!error.details}
          rows={4}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.23)',
            },
          }}
        />
      </Grid>
      <Grid container spacing={2} direction='row'>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth required error={!!error.importance}>
            <InputLabel 
              id='task-importance-input' 
              error={!!error.importance}
              >
                Importance
              </InputLabel>
            <Select
              labelId='task-importance'
              value={formData.importance ?? ''}
              label='Importance'
              onChange={handleSelectChange}
              error={!!error.importance}
              name='importance'
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
            >
              <MenuItem value='Optional' id='optional_select'>
                Optional
              </MenuItem>
              <MenuItem value='Required' id='required_select'>
                Required
              </MenuItem>
            </Select>
            <FormHelperText>{error.importance}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label='Due Date'
              minDateTime={new Date()}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                  helperText: error.dueDate,
                  error: !!error.dueDate,
                  id: 'due-date-input',
                  onBlur: handleDueDateBlur,
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                  }
                },
                field: { clearable: true },
                actionBar: {
                  actions: ['clear', 'cancel', 'accept'],
                },
              }}
              name='dueDate'
              value={formData.dueDate}
              onChange={handleDateChange}
              onAccept={handleDueDateAccept}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      <Grid>
        <TextField
          fullWidth
          id='task-link-input'
          label='Task Link'
          value={formData.link ?? ''}
          onChange={handleChange}
          onBlur={handleLinkBlur}
          name='link'
          helperText={error.link}
          error={!!error.link}
          placeholder='https://example.com'
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.23)',
            },
          }}
        />
      </Grid>
    </Grid>
  )
}

export default TaskDetails
